import React, { useState, useEffect, useMemo } from 'react';
import {
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  KeyIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClipboardDocumentIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid
} from '@heroicons/react/24/solid';
import axiosInstance from '../api/axiosInstance';
import logger from '../../utils/logger';

const AddUserModal = ({ open, onClose, onSubmit, onSave, availableDomains: suppliedDomains = [], onDomainAdded, showNotification }) => {
  const [assignedDomain, setAssignedDomain] = useState(null);
  const [preparingDomain, setPreparingDomain] = useState(false);
  const [domainError, setDomainError] = useState('');
  const availableDomains = assignedDomain && !suppliedDomains.some(d => String(d.id) === String(assignedDomain.id))
    ? [...suppliedDomains, assignedDomain] : suppliedDomains;

  const preparePlatformDomain = async () => {
    if (preparingDomain) return;
    setPreparingDomain(true);
    setDomainError('');
    try {
      const response = await axiosInstance.post('/api/business/onboarding/platform-domain');
      const domain = response.data?.domain;
      if (!domain?.id || !domain.domainName) throw new Error('The server did not return a domain. Please retry.');
      setAssignedDomain(domain);
      onDomainAdded?.(domain);
      setUserData(prev => ({ ...prev, domainId: domain.id, domain: `@${domain.domainName}` }));
      setErrors(prev => ({ ...prev, domainId: null }));
    } catch (error) {
      setDomainError(error.response?.data?.message || error.message || 'Could not prepare your address. Please retry.');
    } finally { setPreparingDomain(false); }
  };

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };

  const initialUserData = useMemo(() => ({
    firstName: '',
    lastName: '',
    username: '',
    domainId: '',
    domain: '',
    role: 'user',
    department: '',
    jobTitle: '',
    phoneNumber: ''
  }), []);

  const initialPasswordData = {
    generatePassword: true,
    password: generatePassword(),
    confirmPassword: '',
    mustChangePassword: true
  };

  const steps = [
    { id: 'basic', label: 'Basic Info', icon: UserIcon },
    { id: 'email', label: 'Email Setup', icon: EnvelopeIcon },
    { id: 'password', label: 'Password', icon: KeyIcon },
    { id: 'review', label: 'Review', icon: CheckCircleIcon }
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(initialUserData);
  const [errors, setErrors] = useState({});
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [passwordData, setPasswordData] = useState(initialPasswordData);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (!open) {
      setUserData(initialUserData);
      setActiveStep(0);
      setErrors({});
      setCreatedUser(null);
      setCopiedField(null);
    }
  }, [open, initialUserData]);

  useEffect(() => {
    if (open) {
      const initialPassword = generatePassword();
      setPasswordData({
        generatePassword: true,
        password: initialPassword,
        confirmPassword: '',
        mustChangePassword: true
      });
    }
  }, [open]);

  const validateStep = () => {
    const newErrors = {};

    switch (activeStep) {
      case 0:
        if (!userData.firstName?.trim()) {
          newErrors.firstName = 'First name is required';
        }
        if (!userData.lastName?.trim()) {
          newErrors.lastName = 'Last name is required';
        }
        if (!userData.role) {
          newErrors.role = 'Role is required';
        }
        break;

      case 1:
        if (!userData.username?.trim()) {
          newErrors.username = 'Username is required';
        } else if (!/^[a-zA-Z0-9._-]+$/.test(userData.username)) {
          newErrors.username = 'Username can only contain letters, numbers, dots, underscores, and hyphens';
        }
        if (!userData.domainId) {
          newErrors.domainId = 'Domain is required';
        }
        break;

      case 2:
        if (!passwordData.generatePassword) {
          if (!passwordData.password) {
            newErrors.password = 'Password is required';
          } else if (passwordData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
          }
          if (passwordData.password !== passwordData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
          }
        } else {
          if (!passwordData.password) {
            const newPassword = generatePassword();
            setPasswordData(prev => ({
              ...prev,
              password: newPassword
            }));
          }
        }
        break;

      case 3:
        if (!passwordData.password) {
          newErrors.password = 'Password is required';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkUsernameAvailability = async (username, domainId) => {
    if (!username || !domainId) return;

    setIsCheckingUsername(true);
    try {
      const domain = availableDomains?.find(d => d.id === domainId);
      const email = `${username}@${domain.domainName}`;

      const response = await axiosInstance.post(
        '/api/business/users/check-username',
        { email }
      );

      if (!response.data.available) {
        setErrors(prev => ({
          ...prev,
          username: 'This username is already taken'
        }));
        return false;
      }
      return true;
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        username: 'Error checking username availability'
      }));
      return false;
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep()) {
      return;
    }

    if (activeStep === 1) {
      const isAvailable = await checkUsernameAvailability(userData.username, userData.domainId);
      if (!isAvailable) {
        return;
      }
    }

    if (activeStep === steps.length - 1) {
      await handleSubmit();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      if (!validateStep()) {
        return;
      }

      const selectedDomain = availableDomains?.find(d => d.id === userData.domainId);
      if (!selectedDomain) {
        showNotification?.('Please select a domain', 'error');
        return;
      }

      const email = `${userData.username}@${selectedDomain.domainName}`;

      const finalUserData = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: email,
        username: userData.username,
        domainId: userData.domainId,
        role: userData.role,
        department: userData.department || '',
        jobTitle: userData.jobTitle || '',
        phoneNumber: userData.phoneNumber || '',
        password: passwordData.password,
        mustChangePassword: passwordData.mustChangePassword
      };

      setLoading(true);

      const submitFunction = onSubmit || onSave;
      if (typeof submitFunction !== 'function') {
        throw new Error('onSubmit or onSave prop is required and must be a function');
      }

      await submitFunction(finalUserData);

      // Show success step with credentials
      setCreatedUser({
        email: email,
        password: passwordData.password,
        firstName: userData.firstName,
        lastName: userData.lastName,
      });
      setActiveStep(steps.length); // Move to success step
    } catch (error) {
      logger.error('Error creating user:', error);
      // Error notification is handled by parent component
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(passwordData.password);
    showNotification?.('Password copied to clipboard', 'success');
  };

  const handleCopyCredential = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const renderStepContent = () => {
    // Success step — show created user credentials
    if (createdUser) {
      return (
        <div className="flex flex-col items-center py-4">
          <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIconSolid className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            User Created Successfully
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {createdUser.firstName} {createdUser.lastName}'s account is ready
          </p>

          <div className="w-full space-y-3">
            {/* Email */}
            <div className="bg-gray-50 dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Email Address</label>
              <div className="flex items-center justify-between">
                <span className="text-sm font-mono font-medium text-gray-900 dark:text-white">{createdUser.email}</span>
                <button
                  type="button"
                  onClick={() => handleCopyCredential(createdUser.email, 'email')}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 bg-white dark:bg-[#2a2a2a] border border-gray-200 dark:border-gray-600 rounded-md hover:border-orange-300 dark:hover:border-orange-600 transition-colors"
                >
                  {copiedField === 'email' ? (
                    <><CheckCircleIconSolid className="w-3.5 h-3.5 text-green-500" /> Copied</>
                  ) : (
                    <><ClipboardDocumentIcon className="w-3.5 h-3.5" /> Copy</>
                  )}
                </button>
              </div>
            </div>

            {/* Password */}
            <div className="bg-gray-50 dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Password</label>
              <div className="flex items-center justify-between">
                <span className="text-sm font-mono font-medium text-gray-900 dark:text-white">{createdUser.password}</span>
                <button
                  type="button"
                  onClick={() => handleCopyCredential(createdUser.password, 'password')}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 bg-white dark:bg-[#2a2a2a] border border-gray-200 dark:border-gray-600 rounded-md hover:border-orange-300 dark:hover:border-orange-600 transition-colors"
                >
                  {copiedField === 'password' ? (
                    <><CheckCircleIconSolid className="w-3.5 h-3.5 text-green-500" /> Copied</>
                  ) : (
                    <><ClipboardDocumentIcon className="w-3.5 h-3.5" /> Copy</>
                  )}
                </button>
              </div>
            </div>

            {/* Copy All */}
            <button
              type="button"
              onClick={() => handleCopyCredential(`Email: ${createdUser.email}\nPassword: ${createdUser.password}`, 'all')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
            >
              {copiedField === 'all' ? (
                <><CheckCircleIconSolid className="w-4 h-4 text-green-500" /> All Credentials Copied</>
              ) : (
                <><ClipboardDocumentIcon className="w-4 h-4" /> Copy All Credentials</>
              )}
            </button>
          </div>

          <div className="mt-5 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg w-full">
            <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              Save these credentials — the password cannot be retrieved after closing this dialog.
            </p>
          </div>
        </div>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userData.firstName}
                  onChange={(e) => {
                    setUserData({ ...userData, firstName: e.target.value });
                    setErrors(prev => ({ ...prev, firstName: null }));
                  }}
                  className={`w-full px-3 py-2.5 border ${errors.firstName ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 dark:border-gray-700 focus:border-orange-500 focus:ring-orange-200'} rounded-lg focus:ring-2 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 transition-colors`}
                  placeholder="John"
                />
                {errors.firstName && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <ExclamationCircleIcon className="w-3 h-3" />
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userData.lastName}
                  onChange={(e) => {
                    setUserData({ ...userData, lastName: e.target.value });
                    setErrors(prev => ({ ...prev, lastName: null }));
                  }}
                  className={`w-full px-3 py-2.5 border ${errors.lastName ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 dark:border-gray-700 focus:border-orange-500 focus:ring-orange-200'} rounded-lg focus:ring-2 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 transition-colors`}
                  placeholder="Doe"
                />
                {errors.lastName && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <ExclamationCircleIcon className="w-3 h-3" />
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                value={userData.role}
                onChange={(e) => {
                  setUserData({ ...userData, role: e.target.value });
                  setErrors(prev => ({ ...prev, role: null }));
                }}
                className={`w-full px-3 py-2.5 border ${errors.role ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 dark:border-gray-700 focus:border-orange-500 focus:ring-orange-200'} rounded-lg focus:ring-2 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white transition-colors`}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
              </select>
              {errors.role && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <ExclamationCircleIcon className="w-3 h-3" />
                  {errors.role}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Department
                </label>
                <input
                  type="text"
                  value={userData.department}
                  onChange={(e) => setUserData({ ...userData, department: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-500 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 transition-colors"
                  placeholder="Engineering"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Job Title
                </label>
                <input
                  type="text"
                  value={userData.jobTitle}
                  onChange={(e) => setUserData({ ...userData, jobTitle: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-500 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 transition-colors"
                  placeholder="Software Engineer"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Phone Number
              </label>
              <input
                type="tel"
                value={userData.phoneNumber}
                onChange={(e) => setUserData({ ...userData, phoneNumber: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-500 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 transition-colors"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Username <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={userData.username}
                  onChange={(e) => {
                    setUserData({ ...userData, username: e.target.value });
                    setErrors(prev => ({ ...prev, username: null }));
                  }}
                  className={`w-full px-3 py-2.5 pr-10 border ${errors.username ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 dark:border-gray-700 focus:border-orange-500 focus:ring-orange-200'} rounded-lg focus:ring-2 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 transition-colors`}
                  placeholder="john.doe"
                />
                {isCheckingUsername && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent"></div>
                  </div>
                )}
              </div>
              {errors.username && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <ExclamationCircleIcon className="w-3 h-3" />
                  {errors.username}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Domain <span className="text-red-500">*</span>
              </label>
              <select
                value={userData.domainId}
                onChange={(e) => {
                  const selectedDomain = availableDomains?.find(d => d.id === e.target.value);
                  setUserData({
                    ...userData,
                    domainId: e.target.value,
                    domain: selectedDomain ? `@${selectedDomain.domainName}` : ''
                  });
                  setErrors(prev => ({ ...prev, domainId: null }));
                }}
                className={`w-full px-3 py-2.5 border ${errors.domainId ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 dark:border-gray-700 focus:border-orange-500 focus:ring-orange-200'} rounded-lg focus:ring-2 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white transition-colors`}
              >
                <option value="">Select a domain</option>
                {availableDomains && availableDomains.length > 0 ? (
                  availableDomains.map(domain => (
                    <option key={domain.id} value={domain.id}>
                      @{domain.domainName}{domain.platformManaged ? ' · Aiinak-provided' : ''}
                    </option>
                  ))
                ) : (
                  <option disabled>No domains available</option>
                )}
              </select>
              <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <p className="text-sm text-gray-700 dark:text-gray-200">No domain? Use aiinakmail.com.</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Get an address like name@your-workspace.aiinakmail.com. No domain purchase or DNS setup needed.</p>
                <button type="button" onClick={preparePlatformDomain} disabled={preparingDomain}
                  className="mt-2 text-sm font-medium text-orange-700 dark:text-orange-400 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500 disabled:opacity-50">
                  {preparingDomain ? 'Preparing your domain…' : 'Use an Aiinak-provided domain'}
                </button>
                {domainError && <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">{domainError}</p>}
                {assignedDomain && <p role="status" className="mt-2 text-xs text-gray-600 dark:text-gray-300">Ready: {assignedDomain.domainName}</p>}
              </div>
              {errors.domainId && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <ExclamationCircleIcon className="w-3 h-3" />
                  {errors.domainId}
                </p>
              )}
            </div>

            {userData.username && userData.domain && (
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Full email address:</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white font-mono">
                  {userData.username}{userData.domain}
                </p>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-800 rounded-lg flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Generate Password</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Auto-generate a secure password</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={passwordData.generatePassword}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setPasswordData(prev => ({
                      ...prev,
                      generatePassword: checked,
                      password: checked ? generatePassword() : '',
                      confirmPassword: checked ? '' : ''
                    }));
                    setErrors(prev => ({ ...prev, password: null, confirmPassword: null }));
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-[#2a2a2a] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-700 peer-checked:bg-orange-500"></div>
              </label>
            </div>

            {passwordData.generatePassword ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Generated Password
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={passwordData.password}
                    readOnly
                    className="w-full px-3 py-2.5 pr-20 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#222222] text-gray-900 dark:text-white font-mono text-sm"
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPasswordData(prev => ({ ...prev, password: generatePassword() }))}
                      className="p-1.5 text-gray-400 hover:text-orange-600 transition-colors"
                      title="Generate new password"
                    >
                      <SparklesIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={copyPassword}
                      className="p-1.5 text-gray-400 hover:text-orange-600 transition-colors"
                      title="Copy to clipboard"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Make sure to save this password securely
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordData.password}
                      onChange={(e) => {
                        setPasswordData(prev => ({
                          ...prev,
                          password: e.target.value
                        }));
                        setErrors(prev => ({ ...prev, password: null }));
                      }}
                      className={`w-full px-3 py-2.5 pr-10 border ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 dark:border-gray-700 focus:border-orange-500 focus:ring-orange-200'} rounded-lg focus:ring-2 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 transition-colors`}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="w-4 h-4" />
                      ) : (
                        <EyeIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <ExclamationCircleIcon className="w-3 h-3" />
                      {errors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => {
                        setPasswordData(prev => ({
                          ...prev,
                          confirmPassword: e.target.value
                        }));
                        setErrors(prev => ({ ...prev, confirmPassword: null }));
                      }}
                      className={`w-full px-3 py-2.5 pr-10 border ${errors.confirmPassword ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 dark:border-gray-700 focus:border-orange-500 focus:ring-orange-200'} rounded-lg focus:ring-2 focus:outline-none bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 transition-colors`}
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="w-4 h-4" />
                      ) : (
                        <EyeIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <ExclamationCircleIcon className="w-3 h-3" />
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            )}

            <label className="flex items-center space-x-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={passwordData.mustChangePassword}
                onChange={(e) => setPasswordData(prev => ({
                  ...prev,
                  mustChangePassword: e.target.checked
                }))}
                className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                Require password change at first sign in
              </span>
            </label>
          </div>
        );

      case 3:
        return (
          <div className="space-y-3">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-[#222222] dark:to-[#1a1a1a] border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <UserIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Full Name</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {userData.firstName} {userData.lastName}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <EnvelopeIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Email Address</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white font-mono">
                    {userData.username}{userData.domain}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircleIconSolid className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Role</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {userData.role.charAt(0).toUpperCase() + userData.role.slice(1)}
                  </p>
                </div>
              </div>

              {(userData.department || userData.jobTitle) && (
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Position</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {userData.jobTitle || '-'} {userData.department && `• ${userData.department}`}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <KeyIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Password</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {passwordData.generatePassword ? 'Auto-generated secure password' : 'Custom password set'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {passwordData.mustChangePassword ?
                      'User must change password at first sign in' :
                      'No password change required'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                <strong>Ready to create!</strong> Click "Create User" to add this user to your system.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {createdUser ? 'User Created' : 'Add New User'}
            </h2>
            {!createdUser && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Step {activeStep + 1} of {steps.length}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        {!createdUser && (
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isCompleted ? 'bg-orange-500 text-white' :
                      isActive ? 'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400 ring-4 ring-orange-100 dark:ring-orange-900/50' :
                      'bg-gray-100 dark:bg-[#2a2a2a] text-gray-400'
                    }`}>
                      {isCompleted ? (
                        <CheckCircleIconSolid className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <span className={`text-xs mt-2 font-medium transition-colors ${
                      isActive ? 'text-orange-600 dark:text-orange-400' :
                      isCompleted ? 'text-gray-700 dark:text-gray-300' :
                      'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 transition-colors ${
                      isCompleted ? 'bg-orange-500' : 'bg-gray-200 dark:bg-[#2a2a2a]'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#111111]">
          {createdUser ? (
            <>
              <div />
              <button
                onClick={onClose}
                className="flex items-center gap-2 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                disabled={activeStep === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeStep === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back
              </button>

              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNext}
                  disabled={loading}
                  className="flex items-center gap-2 px-5 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors min-w-[120px] justify-center"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : activeStep === steps.length - 1 ? (
                    <>
                      Create User
                      <CheckCircleIcon className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRightIcon className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
